import { useEffect } from 'react'

interface Options {
  enabled?: boolean
  edgeSize?: number
  minDistance?: number
  maxVerticalDrift?: number
}

// Detects a right-swipe that starts within the left edge of the screen and
// invokes `onTrigger`. Intended for tab-root pages where the back gesture is
// disabled and the swipe should instead open the side menu.
export const useEdgeSwipeOpen = (
  onTrigger: () => void,
  { enabled = true, edgeSize = 20, minDistance = 60, maxVerticalDrift = 50 }: Options = {},
): void => {
  useEffect(() => {
    if (!enabled) return

    let startX = 0
    let startY = 0
    let tracking = false

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t) return
      if (t.clientX <= edgeSize) {
        startX = t.clientX
        startY = t.clientY
        tracking = true
      } else {
        tracking = false
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking) return
      const t = e.touches[0]
      if (!t) return
      const dx = t.clientX - startX
      const dy = Math.abs(t.clientY - startY)
      if (dy > maxVerticalDrift) {
        tracking = false
        return
      }
      if (dx >= minDistance) {
        tracking = false
        onTrigger()
      }
    }

    const onTouchEnd = () => {
      tracking = false
    }

    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })
    window.addEventListener('touchend', onTouchEnd, { passive: true })
    window.addEventListener('touchcancel', onTouchEnd, { passive: true })

    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [enabled, edgeSize, minDistance, maxVerticalDrift, onTrigger])
}
